import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  IconButton,
  Stack,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { Modify } from '@shared/utilities/UtilityTypes';
import { HSpacer, Text } from '@/components/DesignSystem';
import HelpOutline from "@mui/icons-material/HelpOutline";
import { styled } from "@mui/material/styles";

type InfoTooltipProps = Modify<
  Omit<TooltipProps, 'title'>,
  {
    backgroundColor?: string,
    boundaryRef?: React.RefObject<HTMLElement>,
    children: ReactNode,
    variant?: 'info' | 'help',
    iconColor?: string,
    openOnClick?: boolean,
  }
>;

const GrowersTooltip = styled(({ className, backgroundColor: _, ...props }
: TooltipProps & { backgroundColor?: string }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))<{ backgroundColor?: string }>(({ theme, backgroundColor: bgColor }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: bgColor || theme.palette.secondary.main,
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 8px 12px 6px rgba(0, 0, 0, 0.15), 0 4px 4px 0 rgba(0, 0, 0, 0.30)',
    color: theme.palette.getContrastText(bgColor || theme.palette.secondary.main),
  },
}));

export const InfoTooltip = ({
  backgroundColor,
  boundaryRef,
  children,
  variant = 'info',
  iconColor,
  openOnClick = false,
  ...rest
}: InfoTooltipProps) => {
  const [open, setOpen] = useState(false);

  if (typeof children === 'string') {
    children = <Text category="body-medium">{children}</Text>;
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const defaultModifiers = [
    {
      name: 'offset',
      options: {
        offset: [0, -14],
      },
    },
  ];

  const boundaryModifiers = boundaryRef ? [
    {
      name: 'preventOverflow',
      options: {
        boundary: boundaryRef.current || 'clippingParents',
        padding: 16,
      },
    },
    {
      name: 'flip',
      enabled: true,
    },
  ] : [];

  return (
    <GrowersTooltip
      PopperProps={{
        modifiers: [
          ...defaultModifiers,
          ...boundaryModifiers,
        ],
      }}
      backgroundColor={backgroundColor}
      disableHoverListener={openOnClick}
      disableTouchListener={openOnClick}
      onClose={openOnClick ? handleClose : undefined}
      open={openOnClick ? open : undefined}
      placement="bottom-start"
      title={children}
      {...rest}
    >
      {variant === 'info'
        ? (
          <IconButton
            onClick={openOnClick ? handleClick : undefined}
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
              height: 18,
              width: 18,
            }}
          >
            <InfoOutlined sx={{ height: 18, width: 18, color: iconColor || 'inherit' }} />
          </IconButton>
        )
        : (
          <Stack alignItems="center" direction="row" justifyContent="end">
            <Text category="label-medium">
              What's this
            </Text>
            <HSpacer size="2" />
            <HelpOutline sx={{ width: '15px' }} />
          </Stack>
        )
      }
    </GrowersTooltip>
  );
};
